import React, {
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Box,
  Text,
  Grid,
  GridItem,
  Flex,
  VStack,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import AnalysisCharts from './AnalysisCharts';
import AnalysisContentSection from './AnalysisContentSection';
import AleaEmailSendIcon from '../../../icons/AleaEmailSendIcon';
import { getLanguageKeyByType } from '../../../utils/getLanguageKeyByType';
import { useAuth } from '../../../context/AuthContext';
import { A4ReportDocument, type ReportColumn, type ReportRow, type ChartImage } from '../../../pdf/reports/A4ReportDocument';
import { downloadReactPdf, formatExportTimestamp } from '../../../utils/reactPdfExport';
import { SeniSmartLogo } from '../../../pdf/logos/SeniSmartLogo';
import { captureElementAsImage } from '../../../utils/chartCapture';
import {
  useAnalysisDiapersStatQuery,
  useAnalysisResidentNameQuery,
} from '../analysisPage.queries.ts';

import {
  useUrinationPatternExportMutation,
  useRepositioningHistoryExportMutation,
  useUrineHistoryExportMutation,
} from '../analysisPage.mutations.ts';
import { analysisFilterToIsoRange } from '../analysisFilterDates';
interface FilterState {
  facilityId?: string;
  facilityName?: string;
  startDate?: string;
  endDate?: string;
  stationIds?: string;
  residentIds?: string[];
  stationNames?: Record<string, string>;
  timestamp?: number;
}

interface AnalysisData {
  facility: string;
  period: string;
  livingAreas: string;
  residentCount: number | string;
  ikpChangeCount: number | string;
}

interface AnalysisContentProps {
  data?: AnalysisData[];
  filterState?: FilterState;
  onLoadingChange?: (isLoading: boolean) => void;
}

// Define an interface for the exported methods
export interface AnalysisContentHandles {
  exportToPdf: () => Promise<void>;
}

const LEGACY_EXCLUDE_CATHETER_STORAGE_KEY = 'alea_analysis_exclude_catheter';
const EXCLUDE_CATHETER_OVERALL_STORAGE_KEY =
  'alea_analysis_exclude_catheter_overall';
const EXCLUDE_CATHETER_BY_TIME_STORAGE_KEY =
  'alea_analysis_exclude_catheter_by_time';

const AnalysisContent = forwardRef<
  AnalysisContentHandles,
  AnalysisContentProps
>(({ data = [], filterState = {}, onLoadingChange }, ref) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isExporting, setIsExporting] = useState(false);
  const [excludeCatheterOverall, setExcludeCatheterOverall] =
    useState<boolean>(() => {
      try {
        const stored = localStorage.getItem(
          EXCLUDE_CATHETER_OVERALL_STORAGE_KEY
        );
        if (stored !== null) return stored === 'true';

        const legacy = localStorage.getItem(LEGACY_EXCLUDE_CATHETER_STORAGE_KEY);
        if (legacy !== null) return legacy === 'true';

        return false;
      } catch {
        return false;
      }
    });

  const [excludeCatheterByTime, setExcludeCatheterByTime] =
    useState<boolean>(() => {
      try {
        const stored = localStorage.getItem(EXCLUDE_CATHETER_BY_TIME_STORAGE_KEY);
        if (stored !== null) return stored === 'true';

        const legacy = localStorage.getItem(LEGACY_EXCLUDE_CATHETER_STORAGE_KEY);
        if (legacy !== null) return legacy === 'true';

        return false;
      } catch {
        return false;
      }
    });

  const handleExcludeCatheterOverallChange = (nextExclude: boolean) => {
    setExcludeCatheterOverall(nextExclude);
    try {
      localStorage.setItem(
        EXCLUDE_CATHETER_OVERALL_STORAGE_KEY,
        String(nextExclude)
      );
    } catch {
      return;
    }
  };

  const handleExcludeCatheterByTimeChange = (nextExclude: boolean) => {
    setExcludeCatheterByTime(nextExclude);
    try {
      localStorage.setItem(
        EXCLUDE_CATHETER_BY_TIME_STORAGE_KEY,
        String(nextExclude)
      );
    } catch {
      return;
    }
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    exportToPdf: async () => {
      if (isExporting) return;

      try {
        setIsExporting(true);
        if (onLoadingChange) {
          onLoadingChange(true);
        }

        const exportDate = new Date();
        const exportDay = exportDate.toISOString().slice(0, 10);

        // Wait a bit for charts to render
        await new Promise(resolve => setTimeout(resolve, 500));

        const pdfDocument = await buildPdfDocument(exportDate);

        await downloadReactPdf({
          filename: `analysis-report-${exportDay}.pdf`,
          reactPdfDocument: pdfDocument,
        });
      } catch (error) {
        console.error('Error exporting PDF:', error);
      } finally {
        setIsExporting(false);
        if (onLoadingChange) {
          onLoadingChange(false);
        }
      }
    },
  }));

  const selectedResidentId =
    filterState.residentIds?.length === 1 ? filterState.residentIds[0] : undefined;

  const selectedResidentNameQuery = useAnalysisResidentNameQuery(selectedResidentId);

  const diapersStatQuery = useAnalysisDiapersStatQuery({
    timestamp: filterState.timestamp,
    careHomeId: filterState.facilityId,
    startDate: filterState.startDate,
    endDate: filterState.endDate,
    stationIds: filterState.stationIds,
    residentIds: filterState.residentIds,
  });

  const urinationPatternExportMutation = useUrinationPatternExportMutation();
  const repositioningHistoryExportMutation = useRepositioningHistoryExportMutation();
  const urineHistoryExportMutation = useUrineHistoryExportMutation();
  const carehomeIdForUrinationExport =
    filterState.facilityId ?? user?.carehome?.id;
  const canRequestUrinationPatternExport =
    !!carehomeIdForUrinationExport &&
    carehomeIdForUrinationExport !== '1' &&
    (filterState.residentIds?.length ?? 0) > 0;

  const canRequestRepositioningHistoryExport =
    !!carehomeIdForUrinationExport &&
    carehomeIdForUrinationExport !== '1' &&
    (filterState.residentIds?.length ?? 0) > 0;

  const canRequestUrineHistoryExport = canRequestRepositioningHistoryExport;

  const triggerUrinationPatternExport = (format: 'pdf' | 'xlsx') => {
    if (
      !canRequestUrinationPatternExport ||
      urinationPatternExportMutation.isPending
    ) {
      return;
    }
    urinationPatternExportMutation.mutate({
      carehomeId: carehomeIdForUrinationExport!,
      format,
      residentIds: filterState.residentIds!,
    });
  };

  const triggerRepositioningHistoryExport = () => {
    if (
      !canRequestRepositioningHistoryExport ||
      repositioningHistoryExportMutation.isPending ||
      urineHistoryExportMutation.isPending
    ) {
      return;
    }
    const { startDateIso, endDateIso } = analysisFilterToIsoRange(
      filterState.startDate,
      filterState.endDate
    );
    repositioningHistoryExportMutation.mutate({
      carehomeId: carehomeIdForUrinationExport!,
      residentIds: filterState.residentIds!,
      startDateIso,
      endDateIso,
    });
  };

  const triggerUrineHistoryExport = () => {
    if (
      !canRequestUrineHistoryExport ||
      urineHistoryExportMutation.isPending ||
      repositioningHistoryExportMutation.isPending
    ) {
      return;
    }
    const { startDateIso, endDateIso } = analysisFilterToIsoRange(
      filterState.startDate,
      filterState.endDate
    );
    urineHistoryExportMutation.mutate({
      carehomeId: carehomeIdForUrinationExport!,
      residentIds: filterState.residentIds!,
      startDateIso,
      endDateIso,
    });
  };


  // Responsive padding for the container
  const containerPadding = useBreakpointValue({
    base: '25px', // < 480px
    sm: '25px', // > 480px
    md: '25px', // > 768px
    lg: '50px', // > 992px
  });

  // Responsive settings for different screen sizes
  const isLargeScreen = useBreakpointValue({ base: false, lg: true });

  // Grid template columns based on screen size
  const gridTemplateColumns = isLargeScreen
    ? 'repeat(5, 1fr)' // Larger screens (>992px): use flexible columns
    : '250px 250px 250px 150px 150px'; // Small screens: fixed width columns

  // Min width for grid based on screen size
  const gridMinWidth = isLargeScreen ? 'auto' : '980px';

  // Check if a facility has been selected
  const isFacilitySelected =
    !!filterState.facilityId && filterState.facilityId !== '1';

  // Use date range from filter state or default
  const dateRange =
    filterState.startDate && filterState.endDate
      ? `${filterState.startDate} - ${filterState.endDate}`
      : (() => {
          const today = new Date();
          const weekAgo = new Date();
          weekAgo.setDate(today.getDate() - 7);

          // Format dates to DD.MM.YYYY
          const formatDate = (date: Date): string => {
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();
            return `${day}.${month}.${year}`;
          };

          return `${formatDate(weekAgo)} - ${formatDate(today)}`;
        })();

  // Format living areas names if available
  const getLivingAreasNames = (): string => {
    if (
      !isFacilitySelected ||
      !filterState.stationIds ||
      !filterState.stationNames
    ) {
      return '-';
    }

    const ids = filterState.stationIds.split(',');
    const names = ids.map((id) => filterState.stationNames?.[id] || id);

    // Join names with commas
    return names.join(', ');
  };

  const livingAreasNames = getLivingAreasNames();

  // Use resident count from filter state or default
  const residentCount =
    selectedResidentNameQuery.data ||
    filterState.residentIds?.length ||
    '-';

  const diaperCount: number | string =
    diapersStatQuery.isFetching ? '-' : (diapersStatQuery.data ?? '-');

  // Example data if none is provided
  const exampleData: AnalysisData[] = [
    {
      facility: isFacilitySelected ? filterState.facilityName || `-` : '-',
      period: dateRange,
      livingAreas: isFacilitySelected ? livingAreasNames : '-',
      residentCount: isFacilitySelected ? residentCount : '-',
      ikpChangeCount: diaperCount,
    },
  ];

  const displayData = data.length > 0 ? data : exampleData;

  // Build PDF document using A4ReportDocument layout
  const buildPdfDocument = async (exportDate: Date): Promise<React.ReactElement> => {
    const columns: ReportColumn[] = [
      { key: 'facility', header: t('careHome'), flex: 1, align: 'left' },
      { key: 'period', header: t('analyse.zeitraum'), flex: 1, align: 'left' },
      {
        key: 'livingAreas',
        header: t(
          getLanguageKeyByType(
            'analyse.beteiligteWohnbereiche',
            user?.carehome?.type
          )
        ),
        flex: 1.5,
        align: 'left',
      },
      {
        key: 'residentCount',
        header: t(
          getLanguageKeyByType('analyse.anzahlBewohner', user?.carehome?.type)
        ),
        flex: 1,
        align: 'left',
      },
      {
        key: 'ikpChangeCount',
        header: t('analyse.anzahlIkpWechsel'),
        flex: 1,
        align: 'left',
      },
    ];

    const rows: ReportRow[] = displayData.map((item) => ({
      facility: item.facility || '-',
      period: item.period || '-',
      livingAreas: item.livingAreas || '-',
      residentCount: String(item.residentCount || '-'),
      ikpChangeCount: String(item.ikpChangeCount || '-'),
    }));

    // Capture chart images
    const charts: ChartImage[] = [];
    
    try {
      // Find chart containers (only chart content, without switches)
      const firstChartContainer = document.querySelector('[data-chart-content="first"]') as HTMLElement;
      const secondChartContainer = document.querySelector('[data-chart-content="second"]') as HTMLElement;

      if (firstChartContainer) {
        const chartImage = await captureElementAsImage(firstChartContainer, {
          scale: 2,
          backgroundColor: '#ffffff',
        });
        
        // Build description based on switch state
        const includeCatheter = !excludeCatheterOverall;
        let catheterText = includeCatheter
          ? t('includingCatheterResidents')
          : t('excludingCatheterResidents');
        
        // Remove all parentheses and extra spaces from translation if present
        catheterText = catheterText.replace(/[()]/g, '').replace(/^\s+|\s+$/g, '');
        
        const description = `${t('analyse.gesamt')} (${catheterText})`;
        
        charts.push({
          title: t('analyse.ikpUtilizationAtChangeTime'),
          description: description,
          imageSrc: chartImage,
        });
      }

      if (secondChartContainer) {
        const chartImage = await captureElementAsImage(secondChartContainer, {
          scale: 2,
          backgroundColor: '#ffffff',
        });
        
        // Build description based on switch state
        const includeCatheter = !excludeCatheterByTime;
        let catheterText = includeCatheter
          ? t('includingCatheterResidents')
          : t('excludingCatheterResidents');
        
        // Remove all parentheses and extra spaces from translation if present
        catheterText = catheterText.replace(/[()]/g, '').replace(/^\s+|\s+$/g, '');
        
        const description = `${t('analyse.perTimeOfDay')} (${catheterText})`;
        
        charts.push({
          title: t('analyse.ikpUtilizationAtChangeTime'),
          description: description,
          imageSrc: chartImage,
        });
      }
    } catch (error) {
      console.error('Error capturing charts:', error);
      // Continue without charts if capture fails
    }

    return (
      <A4ReportDocument
        title={t('general.analyse')}
        headerRightLeadingLogo={{
          src: '/assets/images/logos/alea-cockpit-logo.png',
          height: 27,
        }}
        headerRightLabel={t('exclusively_with')}
        headerRightTrailing={<SeniSmartLogo width={73} height={24} />}
        columns={columns}
        rows={rows}
        organisationName={user?.organisation?.name}
        exportedAtLabel={t('general.export', { defaultValue: 'Export' })}
        exportedAtValue={formatExportTimestamp(exportDate)}
        pageLabel={t('general.page', { defaultValue: 'Page' })}
        charts={charts}
        compactTable={true}
      />
    );
  };

  // Handle loading state change from charts
  const handleLoadingChange = (isLoading: boolean) => {
    if (onLoadingChange) {
      onLoadingChange(isLoading);
    }
  };

  return (
    <Box
      borderRadius="20px"
      overflow="hidden"
      mt="10px"
      width="100%"
    >
      <Flex
        bg="#F3F2F2"
        borderBottom="1px solid #E2E2E2"
        minH="75px"
        py="10px"
        alignItems="space-between"
        flexFlow="column"
        justifyContent="center"
        overflowX={isLargeScreen ? 'visible' : 'auto'}
        width="100%"
      >
        {/* Header row with column titles */}
        <Grid
          templateColumns={gridTemplateColumns}
          py="1px"
          px="50px"
          minW={gridMinWidth}
          width="100%"
          gap="0"
        >
          <GridItem mr="10px">
            <Text fontWeight="300" color="#2B4956" fontSize="15px">
              {t('careHome')}
            </Text>
          </GridItem>
          <GridItem mr="10px">
            <Text
              fontWeight="300"
              color="#2B4956"
              fontSize="15px"
              whiteSpace={isLargeScreen ? 'normal' : 'nowrap'}
            >
              {t('analyse.zeitraum')}
            </Text>
          </GridItem>
          <GridItem mr="10px">
            <Text fontWeight="300" color="#2B4956" fontSize="15px">
              {t(
                getLanguageKeyByType(
                  'analyse.beteiligteWohnbereiche',
                  user?.carehome?.type
                )
              )}
            </Text>
          </GridItem>
          <GridItem mr="10px">
            <Text fontWeight="300" color="#2B4956" fontSize="15px">
              {t(
                getLanguageKeyByType(
                  'analyse.anzahlBewohner',
                  user?.carehome?.type
                )
              )}
            </Text>
          </GridItem>
          <GridItem mr="10px">
            <Text fontWeight="300" color="#2B4956" fontSize="15px">
              {t('analyse.anzahlIkpWechsel')}
            </Text>
          </GridItem>
        </Grid>

        {/* Data */}
        {displayData.map((item, index) => (
          <Grid
            key={index}
            templateColumns={gridTemplateColumns}
            py="1px"
            px="50px"
            borderBottom={
              index < displayData.length - 1 ? '1px solid #E2E2E2' : 'none'
            }
            minW={gridMinWidth}
            width="100%"
            gap="0"
          >
            <GridItem mr="10px">
              <Text
                fontWeight="400"
                fontSize="18px"
                noOfLines={isLargeScreen ? undefined : 1}
              >
                {item.facility}
              </Text>
            </GridItem>
            <GridItem mr="10px">
              <Text
                fontWeight="400"
                fontSize="18px"
                whiteSpace={isLargeScreen ? 'normal' : 'nowrap'}
              >
                {item.period}
              </Text>
            </GridItem>
            <GridItem mr="10px">
              <Text
                fontWeight="400"
                fontSize="18px"
                noOfLines={isLargeScreen ? undefined : 1}
              >
                {item.livingAreas}
              </Text>
            </GridItem>
            <GridItem mr="10px">
              <Text
                fontWeight="400"
                fontSize="18px"
                noOfLines={isLargeScreen ? undefined : 1}
              >
                {item.residentCount}
              </Text>
            </GridItem>
            <GridItem mr="10px">
              <Text
                fontWeight="400"
                fontSize="18px"
                noOfLines={isLargeScreen ? undefined : 1}
              >
                {item.ikpChangeCount}
              </Text>
            </GridItem>
          </Grid>
        ))}
      </Flex>

      {/* Main Content */}
      <Box bg="#fff" p={containerPadding}>
        <VStack align="stretch" spacing="100px" w="100%">
          <AnalysisCharts
            filterState={filterState}
            onLoadingChange={handleLoadingChange}
            excludeCatheterOverall={excludeCatheterOverall}
            excludeCatheterByTime={excludeCatheterByTime}
            onExcludeCatheterOverallChange={handleExcludeCatheterOverallChange}
            onExcludeCatheterByTimeChange={handleExcludeCatheterByTimeChange}
          />
          <AnalysisContentSection
            titleKey="urinationExport.recommendedTimesColumn"
            descriptionKey="UrinationProfileSubline"
            downloadLabelKey="emailPdf"
            secondDownloadLabelKey="emailExcel"
            actionIcon={<AleaEmailSendIcon size="25" color="white" />}
            actionGap="25px"
            exportBusy={urinationPatternExportMutation.isPending}
            exportBlocked={!canRequestUrinationPatternExport}
            onDownloadClick={() => triggerUrinationPatternExport('pdf')}
            onSecondDownloadClick={() => triggerUrinationPatternExport('xlsx')}
          />
          <AnalysisContentSection
            titleKey="protocols"
            descriptionKey="ProtocolsSubline"
            downloadLabelKey="repositionignProtocol.exportAsPdf"
            secondDownloadLabelKey="UrinationProtocol.exportAsPdf"
            actionIcon={<AleaEmailSendIcon size="25" color="white" />}
            actionGap="25px"
            exportBusy={
              repositioningHistoryExportMutation.isPending ||
              urineHistoryExportMutation.isPending
            }
            exportBlocked={!canRequestRepositioningHistoryExport}
            onDownloadClick={triggerRepositioningHistoryExport}
            onSecondDownloadClick={triggerUrineHistoryExport}
          />
        </VStack>
      </Box>
    </Box>
  );
});

export default AnalysisContent;
